import { RouteObject, Navigate } from 'react-router-dom';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

// Regional trial pages - lazy loaded with retry
const Regions = lazyWithRetry(() => import('@/pages/Regions'));
const UnitedStatesTrials = lazyWithRetry(() => import('@/pages/regions/UnitedStatesTrials'));
const EuropeTrials = lazyWithRetry(() => import('@/pages/regions/EuropeTrials'));
const JapanTrials = lazyWithRetry(() => import('@/pages/regions/JapanTrials'));
const AustraliaTrials = lazyWithRetry(() => import('@/pages/regions/AustraliaTrials'));
const ChinaTrials = lazyWithRetry(() => import('@/pages/regions/ChinaTrials'));
const IndiaTrials = lazyWithRetry(() => import('@/pages/regions/IndiaTrials'));
const IndonesiaTrials = lazyWithRetry(() => import('@/pages/regions/IndonesiaTrials'));
const PhilippinesTrials = lazyWithRetry(() => import('@/pages/regions/PhilippinesTrials'));
const ThailandTrials = lazyWithRetry(() => import('@/pages/regions/ThailandTrials'));
const VietnamTrials = lazyWithRetry(() => import('@/pages/regions/VietnamTrials'));
const KoreaTrials = lazyWithRetry(() => import('@/pages/regions/KoreaTrials'));
const MalaysiaTrials = lazyWithRetry(() => import('@/pages/regions/MalaysiaTrials'));
const AfricaTrials = lazyWithRetry(() => import('@/pages/regions/AfricaTrials'));
const SouthAfricaTrials = lazyWithRetry(() => import('@/pages/regions/SouthAfricaTrials'));
const WHOInternationalTrials = lazyWithRetry(() => import('@/pages/regions/WHOInternationalTrials'));

// Middle East & North Africa
const SaudiArabiaTrials = lazyWithRetry(() => import('@/pages/regions/SaudiArabiaTrials'));
const SaudiArabiaDemoScript = lazyWithRetry(() => import('@/pages/regions/SaudiArabiaDemoScript'));
const MENATrials = lazyWithRetry(() => import('@/pages/regions/MENATrials'));
const UAETrials = lazyWithRetry(() => import('@/pages/regions/UAETrials'));
const QatarTrials = lazyWithRetry(() => import('@/pages/regions/QatarTrials'));
const KuwaitTrials = lazyWithRetry(() => import('@/pages/regions/KuwaitTrials'));
const BahrainTrials = lazyWithRetry(() => import('@/pages/regions/BahrainTrials'));
const OmanTrials = lazyWithRetry(() => import('@/pages/regions/OmanTrials'));

// Latin America
const LatinAmericaTrials = lazyWithRetry(() => import('@/pages/regions/LatinAmericaTrials'));
const BrazilTrials = lazyWithRetry(() => import('@/pages/regions/BrazilTrials'));
const MexicoTrials = lazyWithRetry(() => import('@/pages/regions/MexicoTrials'));
const ArgentinaTrials = lazyWithRetry(() => import('@/pages/regions/ArgentinaTrials'));
const ChileTrials = lazyWithRetry(() => import('@/pages/regions/ChileTrials'));
const ColombiaTrials = lazyWithRetry(() => import('@/pages/regions/ColombiaTrials'));
const PeruTrials = lazyWithRetry(() => import('@/pages/regions/PeruTrials'));
const VenezuelaTrials = lazyWithRetry(() => import('@/pages/regions/VenezuelaTrials'));
const EcuadorTrials = lazyWithRetry(() => import('@/pages/regions/EcuadorTrials'));
const BoliviaTrials = lazyWithRetry(() => import('@/pages/regions/BoliviaTrials'));
const UruguayTrials = lazyWithRetry(() => import('@/pages/regions/UruguayTrials'));
const ParaguayTrials = lazyWithRetry(() => import('@/pages/regions/ParaguayTrials'));
const CapeTownTrials = lazyWithRetry(() => import('@/pages/regions/CapeTownTrials'));

// Trial Sites by region
const AfricaTrialSites = lazyWithRetry(() => import('@/pages/trial-sites/AfricaTrialSites'));
const SouthAfricaTrialSites = lazyWithRetry(() => import('@/pages/trial-sites/SouthAfricaTrialSites'));
const KenyaTrialSites = lazyWithRetry(() => import('@/pages/trial-sites/KenyaTrialSites'));
const NigeriaTrialSites = lazyWithRetry(() => import('@/pages/trial-sites/NigeriaTrialSites'));
const GhanaTrialSites = lazyWithRetry(() => import('@/pages/trial-sites/GhanaTrialSites'));
const RwandaTrialSites = lazyWithRetry(() => import('@/pages/trial-sites/RwandaTrialSites'));

// Condition + Location pages
const HIVAfrica = lazyWithRetry(() => import('@/pages/trials/HIVAfrica'));
const TuberculosisAfrica = lazyWithRetry(() => import('@/pages/trials/TuberculosisAfrica'));
const MalariaAfrica = lazyWithRetry(() => import('@/pages/trials/MalariaAfrica'));
const HIVSouthAfrica = lazyWithRetry(() => import('@/pages/trials/HIVSouthAfrica'));
const TuberculosisSouthAfrica = lazyWithRetry(() => import('@/pages/trials/TuberculosisSouthAfrica'));
const MalariaSouthAfrica = lazyWithRetry(() => import('@/pages/trials/MalariaSouthAfrica'));
const HIVKenya = lazyWithRetry(() => import('@/pages/trials/HIVKenya'));
const TuberculosisKenya = lazyWithRetry(() => import('@/pages/trials/TuberculosisKenya'));
const MalariaKenya = lazyWithRetry(() => import('@/pages/trials/MalariaKenya'));
const HIVNigeria = lazyWithRetry(() => import('@/pages/trials/HIVNigeria'));
const TuberculosisNigeria = lazyWithRetry(() => import('@/pages/trials/TuberculosisNigeria'));
const MalariaNigeria = lazyWithRetry(() => import('@/pages/trials/MalariaNigeria'));
const HIVGhana = lazyWithRetry(() => import('@/pages/trials/HIVGhana'));
const TuberculosisGhana = lazyWithRetry(() => import('@/pages/trials/TuberculosisGhana'));
const MalariaGhana = lazyWithRetry(() => import('@/pages/trials/MalariaGhana'));

// SEO pillar + gap-fill (oncology x country, maturity-3 corridor, untapped NCDs, vaccines)
const AfricaSiteActivation2026 = lazyWithRetry(() => import('@/pages/playbook/AfricaSiteActivation2026'));
const OncologyAfrica = lazyWithRetry(() => import('@/pages/trials/OncologyAfrica'));
const OncologySouthAfrica = lazyWithRetry(() => import('@/pages/trials/OncologySouthAfrica'));
const OncologyNigeria = lazyWithRetry(() => import('@/pages/trials/OncologyNigeria'));
const OncologyKenya = lazyWithRetry(() => import('@/pages/trials/OncologyKenya'));
const OncologyGhana = lazyWithRetry(() => import('@/pages/trials/OncologyGhana'));
const HIVUganda = lazyWithRetry(() => import('@/pages/trials/HIVUganda'));
const HIVTanzania = lazyWithRetry(() => import('@/pages/trials/HIVTanzania'));
const TuberculosisUganda = lazyWithRetry(() => import('@/pages/trials/TuberculosisUganda'));
const TuberculosisTanzania = lazyWithRetry(() => import('@/pages/trials/TuberculosisTanzania'));
const VaccinesAfrica = lazyWithRetry(() => import('@/pages/trials/VaccinesAfrica'));
const SickleCellAfrica = lazyWithRetry(() => import('@/pages/trials/SickleCellAfrica'));
const DiabetesAfrica = lazyWithRetry(() => import('@/pages/trials/DiabetesAfrica'));

// Condition pages
const ConditionsHub = lazyWithRetry(() => import('@/pages/conditions/ConditionsHub'));
const DiabetesTrials = lazyWithRetry(() => import('@/pages/conditions/DiabetesTrials'));
const CancerTrials = lazyWithRetry(() => import('@/pages/conditions/CancerTrials'));
const HeartDiseaseTrials = lazyWithRetry(() => import('@/pages/conditions/HeartDiseaseTrials'));
const AlzheimersTrials = lazyWithRetry(() => import('@/pages/conditions/AlzheimersTrials'));
const RareDiseaseTrials = lazyWithRetry(() => import('@/pages/conditions/RareDiseaseTrials'));
const RheumatoidArthritisTrials = lazyWithRetry(() => import('@/pages/conditions/RheumatoidArthritisTrials'));

// Oncology landing
const OncologyTrialsLanding = lazyWithRetry(() => import('@/pages/conditions/OncologyTrialsLanding'));

// Cancer subcategories
const BreastCancerTrials = lazyWithRetry(() => import('@/pages/conditions/cancer/BreastCancerTrials'));
const LungCancerTrials = lazyWithRetry(() => import('@/pages/conditions/cancer/LungCancerTrials'));
const ColorectalCancerTrials = lazyWithRetry(() => import('@/pages/conditions/cancer/ColorectalCancerTrials'));
const ProstateCancerTrials = lazyWithRetry(() => import('@/pages/conditions/cancer/ProstateCancerTrials'));
const MelanomaTrials = lazyWithRetry(() => import('@/pages/conditions/cancer/MelanomaTrials'));
const LeukemiaLymphomaTrials = lazyWithRetry(() => import('@/pages/conditions/cancer/LeukemiaLymphomaTrials'));

// Diabetes subcategories
const Type1DiabetesTrials = lazyWithRetry(() => import('@/pages/conditions/diabetes/Type1DiabetesTrials'));
const Type2DiabetesTrials = lazyWithRetry(() => import('@/pages/conditions/diabetes/Type2DiabetesTrials'));
const GestationalDiabetesTrials = lazyWithRetry(() => import('@/pages/conditions/diabetes/GestationalDiabetesTrials'));

// Heart disease subcategories
const CoronaryArteryDiseaseTrials = lazyWithRetry(() => import('@/pages/conditions/heart/CoronaryArteryDiseaseTrials'));
const HeartFailureTrials = lazyWithRetry(() => import('@/pages/conditions/heart/HeartFailureTrials'));
const ArrhythmiaTrials = lazyWithRetry(() => import('@/pages/conditions/heart/ArrhythmiaTrials'));
const HypertensionTrials = lazyWithRetry(() => import('@/pages/conditions/heart/HypertensionTrials'));

// Alzheimer's subcategories
const AlzheimersDiseaseTrials = lazyWithRetry(() => import('@/pages/conditions/alzheimers/AlzheimersDiseaseTrials'));
const VascularDementiaTrials = lazyWithRetry(() => import('@/pages/conditions/alzheimers/VascularDementiaTrials'));
const MildCognitiveImpairmentTrials = lazyWithRetry(() => import('@/pages/conditions/alzheimers/MildCognitiveImpairmentTrials'));

// Arthritis subcategories
const PsoriaticArthritisTrials = lazyWithRetry(() => import('@/pages/conditions/arthritis/PsoriaticArthritisTrials'));
const OsteoarthritisTrials = lazyWithRetry(() => import('@/pages/conditions/arthritis/OsteoarthritisTrials'));

// Marketing landing pages
const ClinicalTrialFinder = lazyWithRetry(() => import('@/pages/ClinicalTrialFinder'));
const MyTrials = lazyWithRetry(() => import('@/pages/MyTrials'));
const PatientRecruitment = lazyWithRetry(() => import('@/pages/PatientRecruitment'));
const ClinicalResearch = lazyWithRetry(() => import('@/pages/ClinicalResearch'));
const DatasetsLanding = lazyWithRetry(() => import('@/pages/DatasetsLanding'));

// Public SEO trial pages (Phase 1)
const PublicTrialPage = lazyWithRetry(() => import('@/pages/public/PublicTrialPage'));
const PublicTrialsIndexPage = lazyWithRetry(() => import('@/pages/public/PublicTrialsIndexPage'));

// Paid clinical trials directory (SEO)
const PaidStudiesIndex = lazyWithRetry(() => import('@/pages/trials/paid/PaidStudiesIndex'));
const PaidStudiesCountry = lazyWithRetry(() => import('@/pages/trials/paid/PaidStudiesCountry'));
const PaidStudiesCity = lazyWithRetry(() => import('@/pages/trials/paid/PaidStudiesCity'));

/**
 * Regional and condition-specific trial routes
 */
export const regionalRoutes: RouteObject[] = [
  // Regional hub
  { path: '/regions', element: <Regions /> },

  // Public SEO trial pages — African recruiting trials only (server-enforced)
  { path: '/clinical-trials', element: <PublicTrialsIndexPage /> },
  { path: '/clinical-trials/:nctId', element: <PublicTrialPage /> },

  // Paid clinical trials directory — country + city landing pages for "paid studies near me" search intent
  { path: '/trials/paid', element: <PaidStudiesIndex /> },
  { path: '/trials/paid/:country', element: <PaidStudiesCountry /> },
  { path: '/trials/paid/:country/:city', element: <PaidStudiesCity /> },

  // Major regions
  { path: '/regions/united-states', element: <UnitedStatesTrials /> },
  { path: '/regions/europe', element: <EuropeTrials /> },
  { path: '/regions/japan', element: <JapanTrials /> },
  { path: '/regions/australia-new-zealand', element: <AustraliaTrials /> },
  { path: '/regions/china', element: <ChinaTrials /> },
  { path: '/regions/india', element: <IndiaTrials /> },
  { path: '/regions/indonesia', element: <IndonesiaTrials /> },
  { path: '/regions/philippines', element: <PhilippinesTrials /> },
  { path: '/regions/thailand', element: <ThailandTrials /> },
  { path: '/regions/vietnam', element: <VietnamTrials /> },
  { path: '/regions/korea', element: <KoreaTrials /> },
  { path: '/regions/malaysia', element: <MalaysiaTrials /> },
  { path: '/regions/africa', element: <AfricaTrials /> },
  { path: '/regions/south-africa', element: <SouthAfricaTrials /> },
  { path: '/regions/who-international', element: <WHOInternationalTrials /> },
  
  // Middle East & North Africa
  { path: '/regions/mena', element: <MENATrials /> },
  { path: '/regions/saudi-arabia', element: <SaudiArabiaTrials /> },
  { path: '/regions/saudi-arabia/demo-script', element: <SaudiArabiaDemoScript /> },
  { path: '/regions/uae', element: <UAETrials /> },
  { path: '/regions/qatar', element: <QatarTrials /> },
  { path: '/regions/kuwait', element: <KuwaitTrials /> },
  { path: '/regions/bahrain', element: <BahrainTrials /> },
  { path: '/regions/oman', element: <OmanTrials /> },
  
  // Latin America
  { path: '/regions/latin-america', element: <LatinAmericaTrials /> },
  { path: '/regions/brazil', element: <BrazilTrials /> },
  { path: '/regions/mexico', element: <MexicoTrials /> },
  { path: '/regions/argentina', element: <ArgentinaTrials /> },
  { path: '/regions/chile', element: <ChileTrials /> },
  { path: '/regions/colombia', element: <ColombiaTrials /> },
  { path: '/regions/peru', element: <PeruTrials /> },
  { path: '/regions/venezuela', element: <VenezuelaTrials /> },
  { path: '/regions/ecuador', element: <EcuadorTrials /> },
  { path: '/regions/bolivia', element: <BoliviaTrials /> },
  { path: '/regions/uruguay', element: <UruguayTrials /> },
  { path: '/regions/paraguay', element: <ParaguayTrials /> },
  { path: '/regions/cape-town', element: <CapeTownTrials /> },
  
  // Trial Sites by Location
  { path: '/trial-sites/africa', element: <AfricaTrialSites /> },
  { path: '/trial-sites/south-africa', element: <SouthAfricaTrialSites /> },
  { path: '/trial-sites/kenya', element: <KenyaTrialSites /> },
  { path: '/trial-sites/nigeria', element: <NigeriaTrialSites /> },
  { path: '/trial-sites/ghana', element: <GhanaTrialSites /> },
  { path: '/trial-sites/rwanda', element: <RwandaTrialSites /> },
  
  // Condition + Location combinations
  { path: '/trials/hiv/africa', element: <HIVAfrica /> },
  { path: '/trials/tuberculosis/africa', element: <TuberculosisAfrica /> },
  { path: '/trials/malaria/africa', element: <MalariaAfrica /> },
  { path: '/trials/hiv/south-africa', element: <HIVSouthAfrica /> },
  { path: '/trials/tuberculosis/south-africa', element: <TuberculosisSouthAfrica /> },
  { path: '/trials/malaria/south-africa', element: <MalariaSouthAfrica /> },
  { path: '/trials/hiv/kenya', element: <HIVKenya /> },
  { path: '/trials/tuberculosis/kenya', element: <TuberculosisKenya /> },
  { path: '/trials/malaria/kenya', element: <MalariaKenya /> },
  { path: '/trials/hiv/nigeria', element: <HIVNigeria /> },
  { path: '/trials/tuberculosis/nigeria', element: <TuberculosisNigeria /> },
  { path: '/trials/malaria/nigeria', element: <MalariaNigeria /> },
  { path: '/trials/hiv/ghana', element: <HIVGhana /> },
  { path: '/trials/tuberculosis/ghana', element: <TuberculosisGhana /> },
  { path: '/trials/malaria/ghana', element: <MalariaGhana /> },

  // SEO pillar + gap-fill routes
  { path: '/playbook/africa-site-activation-2026', element: <AfricaSiteActivation2026 /> },
  { path: '/trials/oncology/africa', element: <OncologyAfrica /> },
  { path: '/trials/oncology/south-africa', element: <OncologySouthAfrica /> },
  { path: '/trials/oncology/nigeria', element: <OncologyNigeria /> },
  { path: '/trials/oncology/kenya', element: <OncologyKenya /> },
  { path: '/trials/oncology/ghana', element: <OncologyGhana /> },
  { path: '/trials/hiv/uganda', element: <HIVUganda /> },
  { path: '/trials/hiv/tanzania', element: <HIVTanzania /> },
  { path: '/trials/tuberculosis/uganda', element: <TuberculosisUganda /> },
  { path: '/trials/tuberculosis/tanzania', element: <TuberculosisTanzania /> },
  { path: '/trials/vaccines/africa', element: <VaccinesAfrica /> },
  { path: '/trials/sickle-cell/africa', element: <SickleCellAfrica /> },
  { path: '/trials/diabetes/africa', element: <DiabetesAfrica /> },
  
  // Condition-specific pages
  { path: '/conditions', element: <ConditionsHub /> },
  { path: '/conditions/diabetes', element: <DiabetesTrials /> },
  { path: '/conditions/cancer', element: <CancerTrials /> },
  { path: '/conditions/heart-disease', element: <HeartDiseaseTrials /> },
  { path: '/conditions/alzheimers', element: <AlzheimersTrials /> },
  { path: '/conditions/rare-diseases', element: <RareDiseaseTrials /> },
  { path: '/conditions/rheumatoid-arthritis', element: <RheumatoidArthritisTrials /> },
  
  // Oncology landing
  { path: '/conditions/oncology', element: <OncologyTrialsLanding /> },

  // Cancer subcategories
  { path: '/conditions/cancer/breast-cancer', element: <BreastCancerTrials /> },
  { path: '/conditions/cancer/lung-cancer', element: <LungCancerTrials /> },
  { path: '/conditions/cancer/colorectal', element: <ColorectalCancerTrials /> },
  { path: '/conditions/cancer/prostate-cancer', element: <ProstateCancerTrials /> },
  { path: '/conditions/cancer/melanoma', element: <MelanomaTrials /> },
  { path: '/conditions/cancer/leukemia-lymphoma', element: <LeukemiaLymphomaTrials /> },
  
  // Diabetes subcategories
  { path: '/conditions/diabetes/type1-diabetes', element: <Type1DiabetesTrials /> },
  { path: '/conditions/diabetes/type2-diabetes', element: <Type2DiabetesTrials /> },
  { path: '/conditions/diabetes/gestational-diabetes', element: <GestationalDiabetesTrials /> },
  
  // Heart disease subcategories
  { path: '/conditions/heart/coronary-artery-disease', element: <CoronaryArteryDiseaseTrials /> },
  { path: '/conditions/heart/heart-failure', element: <HeartFailureTrials /> },
  { path: '/conditions/heart/arrhythmia', element: <ArrhythmiaTrials /> },
  { path: '/conditions/heart/hypertension', element: <HypertensionTrials /> },
  
  // Alzheimer's subcategories
  { path: '/conditions/alzheimers/alzheimers-disease', element: <AlzheimersDiseaseTrials /> },
  { path: '/conditions/alzheimers/vascular-dementia', element: <VascularDementiaTrials /> },
  { path: '/conditions/alzheimers/mci', element: <MildCognitiveImpairmentTrials /> },
  
  // Arthritis subcategories
  { path: '/conditions/arthritis/psoriatic-arthritis', element: <PsoriaticArthritisTrials /> },
  { path: '/conditions/arthritis/osteoarthritis', element: <OsteoarthritisTrials /> },
  
  // Marketing landing pages
  { path: '/clinical-trial-finder', element: <ClinicalTrialFinder /> },
  { path: '/my-trials', element: <MyTrials /> },
  { path: '/trial-sites/recruitment', element: <PatientRecruitment /> },
  { path: '/clinical-research', element: <ClinicalResearch /> },
  { path: '/datasets', element: <DatasetsLanding /> },
];

// Export lazy components for prefetching
export const regionalRoutePrefetch = {
  Regions,
  AfricaTrials,
  SouthAfricaTrials,
  LatinAmericaTrials,
  ConditionsHub,
};
